import { apiConfig } from '@/config'

const accessTokenKey = 'accessToken'

const mcpOAuthRequestIdPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

type OAuthErrorResponse = {
  error_description?: string
}

export type McpOAuthErrorCode =
  | 'expired'
  | 'sessionExpired'
  | 'invalidRedirect'

export class McpOAuthError extends Error {
  readonly code: McpOAuthErrorCode

  constructor(code: McpOAuthErrorCode) {
    super(code)
    this.name = 'McpOAuthError'
    this.code = code
  }
}

export type McpOAuthAuthorizationRequest = {
  clientName: string
  scopes: string[]
}

export function isMcpOAuthRequestId(
  value: string | null | undefined
): value is string {
  return typeof value === 'string' && mcpOAuthRequestIdPattern.test(value)
}

function readAccessToken(): string {
  const accessToken = window.localStorage.getItem(accessTokenKey)
  if (!accessToken) {
    throw new McpOAuthError('sessionExpired')
  }
  return accessToken
}

function isHttpRedirectUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function getGatewayUrl(path: string): string {
  return `${apiConfig().apiUrl.replace(/\/api$/, '')}${path}`
}

async function throwOAuthError(response: Response): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as OAuthErrorResponse
  if (body.error_description) {
    throw new Error(body.error_description)
  }
  throw new McpOAuthError('expired')
}

export async function getMcpOAuthAuthorizationRequest(
  requestId: string
): Promise<McpOAuthAuthorizationRequest> {
  const response = await fetch(
    getGatewayUrl(
      `/mcp/oauth/authorize/requests/${encodeURIComponent(requestId)}`
    ),
    {
      headers: {
        Authorization: `Bearer ${readAccessToken()}`,
      },
    }
  )
  if (!response.ok) {
    return throwOAuthError(response)
  }
  return (await response.json()) as McpOAuthAuthorizationRequest
}

export async function completeMcpOAuthAuthorization(
  requestId: string,
  approved: boolean
): Promise<string> {
  const response = await fetch(getGatewayUrl('/mcp/oauth/authorize/complete'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${readAccessToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ requestId, approved }),
  })
  if (!response.ok) {
    return throwOAuthError(response)
  }

  const body = (await response.json()) as { redirectUrl?: unknown }
  if (
    typeof body.redirectUrl !== 'string' ||
    !isHttpRedirectUrl(body.redirectUrl)
  ) {
    throw new McpOAuthError('invalidRedirect')
  }
  return body.redirectUrl
}
